import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Search,
  MoreVertical,
  Send,
  Phone,
  Video,
  User,
  Check,
  CheckCheck,
  AlertCircle,
  Loader2,
  Download,
  Paperclip,
  X,
  Image as ImageIcon,
  Music,
  File,
  Bot,
} from "lucide-react";
import { sendMessage, sendMediaMessage, checkWindowStatus, downloadMedia, uploadChatMedia, markMessagesAsRead, getChatAiStatus, toggleChatAiStatus } from "../apis/chat";
import { getSocket } from "../utils/socket";
import { useLiveChat } from "../contexts/LiveChatContext";
import moment from "moment-timezone";

const LiveChats = () => {
  // Get state and functions from context
  const {
    chats,
    selectedChat,
    messages,
    loading,
    messagesLoading,
    selectedPhoneId,
    selectChat,
    setSelectedPhoneId,
    updateMessage,
  } = useLiveChat();

  // Local UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isWindowOpen, setIsWindowOpen] = useState(true);
  const [windowWarning, setWindowWarning] = useState(false);
  const [downloadingMedia, setDownloadingMedia] = useState(new Set());
  const [chatAiEnabled, setChatAiEnabled] = useState(false);
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Format timestamp helper
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const now = moment().tz("Asia/Kolkata");
    const msgTime = moment(timestamp).tz("Asia/Kolkata");
    const diffMinutes = now.diff(msgTime, "minutes");
    const diffHours = now.diff(msgTime, "hours");
    const diffDays = now.diff(msgTime, "days");

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return msgTime.format("MMM DD, YYYY");
  };

  // Initialize socket for additional events (status updates, media downloads)
  useEffect(() => {
    const socket = getSocket();
    if (!socket) {
      console.warn("Socket not available");
      return;
    }

    // Listen for message status updates
    socket.on("message_status_update", (data) => {
      // Status updates handled in context if needed
    });

    // Listen for media download completion
    socket.on("media_downloaded", (data) => {
      console.log("Media downloaded event received:", data);
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(data.message_id);
        return next;
      });
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.off("message_status_update");
        socket.off("media_downloaded");
      }
    };
  }, []);

  // Fetch AI status for a chat
  const fetchChatAiStatus = async (customerPhone, phoneNumberId) => {
    try {
      const response = await getChatAiStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setChatAiEnabled(response.data.data.ai_enabled || false);
      }
    } catch (error) {
      console.error("Error fetching chat AI status:", error);
      setChatAiEnabled(false); // Default to disabled on error
    }
  };

  // Toggle AI status for a chat
  const handleToggleChatAi = async () => {
    if (!selectedChat || aiToggleLoading) return;
    
    const newStatus = !chatAiEnabled;
    setAiToggleLoading(true);
    
    try {
      const response = await toggleChatAiStatus(
        selectedChat.customer_phone,
        selectedChat.phone_number_id,
        newStatus
      );
      
      if (response.data.success) {
        setChatAiEnabled(newStatus);
      } else {
        console.error("Failed to toggle AI status");
      }
    } catch (error) {
      console.error("Error toggling chat AI status:", error);
    } finally {
      setAiToggleLoading(false);
    }
  };

  // Handle chat selection
  const handleChatSelect = async (chat) => {
    console.log("Chat selected:", chat);
    if (!chat.customer_phone || !chat.phone_number_id) {
      console.error("Missing chat data:", chat);
      return;
    }
    
    setIsWindowOpen(chat.is_window_open);
    
    // Mark messages as read when opening chat
    markMessagesAsRead(chat.customer_phone, chat.phone_number_id)
      .catch((err) => console.error("Error marking messages as read:", err));

    // Select chat (this will fetch messages via context)
    selectChat(chat);
    
    // Fetch AI status for this chat
    await fetchChatAiStatus(chat.customer_phone, chat.phone_number_id);
    
    // Check window status
    checkConversationWindow(chat.customer_phone, chat.phone_number_id);
    
    // Scroll to bottom after messages load (newest at bottom)
    setTimeout(() => scrollToBottom(), 100);
  };

  // Check conversation window status
  const checkConversationWindow = async (customerPhone, phoneNumberId) => {
    try {
      const response = await checkWindowStatus(customerPhone, phoneNumberId);
      if (response.data.success) {
        setIsWindowOpen(response.data.data.is_window_open);
      }
    } catch (error) {
      console.error("Error checking window status:", error);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert("File size must be less than 50MB");
      return;
    }

    setSelectedFile(file);

    // Create preview
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "image", url: e.target.result });
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "video", url: e.target.result });
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("audio/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: "audio", url: e.target.result });
      reader.readAsDataURL(file);
    } else {
      setFilePreview({ type: "document", name: file.name, size: file.size });
    }

    // Reset file input
    e.target.value = "";
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  // Send message (text or media)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!messageText.trim() && !selectedFile) || !selectedChat || sending || uploadingMedia) return;

    // Check if window is open
    if (!isWindowOpen) {
      setWindowWarning(true);
      setTimeout(() => setWindowWarning(false), 5000);
      return;
    }

    try {
      setSending(true);

      if (selectedFile) {
        // Upload file first
        setUploadingMedia(true);
        const uploadResponse = await uploadChatMedia(selectedFile);
        
        if (uploadResponse.data.success) {
          // Send media message
          const response = await sendMediaMessage(
            selectedChat.phone_number_id,
            selectedChat.customer_phone,
            uploadResponse.data.data.media_url,
            uploadResponse.data.data.media_type,
            messageText.trim() || null // Use messageText as caption if provided
          );

          if (response.data.success) {
            setMessageText("");
            setSelectedFile(null);
            setFilePreview(null);
          }
        }
        setUploadingMedia(false);
      } else {
        // Send text message
        const response = await sendMessage(
          selectedChat.phone_number_id,
          selectedChat.customer_phone,
          messageText
        );

        if (response.data.success) {
          setMessageText("");
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (error.response?.data?.error === "CONVERSATION_WINDOW_CLOSED") {
        setWindowWarning(true);
        setIsWindowOpen(false);
        setTimeout(() => setWindowWarning(false), 5000);
      } else {
        alert(error.response?.data?.error || "Failed to send message. Please try again.");
      }
    } finally {
      setSending(false);
      setUploadingMedia(false);
    }
  };

  // Scroll to bottom of messages (newest at bottom)
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  // Scroll to bottom when messages change (to show newest messages)
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Handle media download (async - downloads from Meta and uploads to GCS)
  const handleDownloadMedia = async (message) => {
    // Check if we have media_id or media_url, and not already downloading
    if (!selectedChat || (!message.media_id && !message.media_url) || downloadingMedia.has(message.message_id)) {
      return;
    }

    // If already has downloaded URL, no need to download again
    if (message.downloaded_media_url) {
      return;
    }

    try {
      setDownloadingMedia((prev) => new Set(prev).add(message.message_id));

      // Call backend API - it will:
      // 1. Get media URL from Meta API if we have media_id
      // 2. Download media from Meta
      // 3. Upload to Google Cloud Storage
      // 4. Return GCS URL immediately
      // 5. Store GCS URL in database asynchronously
      const response = await downloadMedia(message.message_id, selectedChat.phone_number_id);
      
      if (response.data.success && response.data.data.downloaded_media_url) {
        const downloadedUrl = response.data.data.downloaded_media_url;
        console.log("✅ Media downloaded and stored in GCS:", downloadedUrl);
        
        // Update message with downloaded URL
        updateMessage(message.message_id, { 
          downloaded_media_url: downloadedUrl,
        });
        
        // Clear downloading state
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      } else {
        // If no URL returned, clear downloading state
        setDownloadingMedia((prev) => {
          const next = new Set(prev);
          next.delete(message.message_id);
          return next;
        });
      }
    } catch (error) {
      console.error("❌ Error downloading media:", error);
      alert("Failed to download media. Please try again.");
      setDownloadingMedia((prev) => {
        const next = new Set(prev);
        next.delete(message.message_id);
        return next;
      });
    }
  };

  // Get message status icon
  const getStatusIcon = (message) => {
    if (message.direction === "inbound") return null;
    
    const status = message.message_status || "sent";
    const statuses = message.statuses || [];
    const hasRead = statuses.some((s) => s.status === "read");
    const hasDelivered = statuses.some((s) => s.status === "delivered");

    if (hasRead) {
      return <CheckCheck size={12} className="text-blue-500" />;
    } else if (hasDelivered) {
      return <CheckCheck size={12} className="text-gray-400" />;
    } else if (status === "sent") {
      return <Check size={12} className="text-gray-400" />;
    }
    return null;
  };

  // Filter chats based on search
  const filteredChats = chats.filter(
    (chat) =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.phone.includes(searchQuery)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 sm:px-4 lg:px-5 py-4">
        {/* Header */}
        <div className="mb-4">
        </div>

        {/* Window Warning */}
        {windowWarning && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertCircle size={16} className="text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Conversation window is closed. You can only send messages within 24 hours of the customer's last message. Please use a template message instead.
            </p>
          </div>
        )}

        {/* Main Chat Container */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex h-[calc(100vh-12rem)]">
            {/* Chat List Sidebar */}
            <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
              {/* Search Bar */}
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white"
                  />
                </div>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto scrollbar-hide">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={30} className="animate-spin text-primary" />
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">
                      {searchQuery ? "No conversations found" : "No conversations yet"}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredChats.map((chat) => (
                      <motion.div
                        key={chat.id}
                        onClick={() => handleChatSelect(chat)}
                        className={`
                          p-3 cursor-pointer transition-colors duration-200
                          ${selectedChat?.id === chat.id ? "bg-primary/5" : "hover:bg-gray-50"}
                        `}
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                      >
                        <div className="flex items-start gap-2.5">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User size={18} className="text-primary" />
                            </div>
                            {!chat.is_window_open && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>

                          {/* Chat Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {chat.name}
                              </h3>
                              <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                                {chat.timestamp}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-600 truncate">
                                {chat.lastMessage}
                              </p>
                              {chat.unread > 0 && (
                                <span className="flex-shrink-0 ml-2 bg-primary text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                                  {chat.unread}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chat Window */}
            <div className="hidden md:flex flex-1 flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User size={18} className="text-primary" />
                        </div>
                        {!isWindowOpen && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {selectedChat.name}
                        </h3>
                        <p className="text-xs text-gray-500">{selectedChat.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!isWindowOpen && (
                        <span className="text-xs text-yellow-600 px-2 py-1 bg-yellow-50 rounded">
                          Window Closed
                        </span>
                      )}
                      {/* AI Toggle Button */}
                      <button
                        onClick={handleToggleChatAi}
                        disabled={aiToggleLoading}
                        className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          chatAiEnabled
                            ? "bg-primary/10 text-primary hover:bg-primary/20"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={chatAiEnabled ? "AI Enabled - Click to disable" : "AI Disabled - Click to enable"}
                      >
                        {aiToggleLoading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Bot size={14} className={chatAiEnabled ? "text-primary" : "text-gray-500"} />
                        )}
                        <span>{chatAiEnabled ? "AI On" : "AI Off"}</span>
                      </button>
                      <button className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
                        <MoreVertical size={16} className="text-gray-600" />
                      </button>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-gray-50 scrollbar-hide">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={30} className="animate-spin text-primary" />
                          </div>
                    ) : messages.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">No messages yet</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((message) => (
                          <div
                            key={message.id || message.message_id}
                            className={`flex ${
                              message.direction === "outbound" ? "justify-end" : "justify-start"
                            }`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-3 py-2 shadow-sm ${
                                message.direction === "outbound"
                                  ? "bg-primary text-white"
                                  : "bg-white text-gray-900"
                              }`}
                            >
                              {/* Button Message Display */}
                              {message.message_type === "button" && (
                                <div className="mb-2">
                                  {message.button ? (
                                    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                                      message.direction === "outbound" 
                                        ? "bg-white/20 border border-white/30 text-white" 
                                        : "bg-gray-100 border border-gray-200 text-gray-700"
                                    }`}>
                                      <span className="text-xs font-medium opacity-80">🔘 Button Clicked:</span>
                                      <span className="text-sm font-semibold">{message.button.text}</span>
                                    </div>
                                  ) : message.message_text ? (
                                    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                                      message.direction === "outbound" 
                                        ? "bg-white/20 border border-white/30 text-white" 
                                        : "bg-gray-100 border border-gray-200 text-gray-700"
                                    }`}>
                                      <span className="text-xs font-medium opacity-80">🔘</span>
                                      <span className="text-sm font-semibold">{message.message_text}</span>
                                    </div>
                                  ) : (
                                    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                                      message.direction === "outbound" 
                                        ? "bg-white/20 border border-white/30 text-white" 
                                        : "bg-gray-100 border border-gray-200 text-gray-700"
                                    }`}>
                                      <span className="text-xs font-medium opacity-80">🔘 Button Interaction</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Media Messages - exclude button type */}
                              {(message.media_url || (message.media_id && message.message_type !== "text" && message.message_type !== "button")) && (
                                <div className="mb-2 relative group">
                                  {/* Download button overlay - shows when media not downloaded (has media_id or media_url but no downloaded_media_url) */}
                                  {!message.downloaded_media_url && (message.media_id || message.media_url) && (
                                    <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center z-10 group-hover:bg-black/50 transition-colors">
                                      <button
                                        onClick={() => handleDownloadMedia(message)}
                                        disabled={downloadingMedia.has(message.message_id)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg font-medium transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                                          message.direction === "outbound"
                                            ? "bg-white text-primary hover:bg-gray-50"
                                            : "bg-primary text-white hover:bg-primary/90"
                                        }`}
                                        title={message.media_id && !message.media_url ? "Download media from Meta" : "Download media to view"}
                                      >
                                        {downloadingMedia.has(message.message_id) ? (
                                          <>
                                            <Loader2 size={18} className="animate-spin" />
                                            <span className="text-sm">Downloading...</span>
                                          </>
                                        ) : (
                                          <>
                                            <Download size={18} />
                                            <span className="text-sm">Download to View</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Show loading overlay during download */}
                                  {downloadingMedia.has(message.message_id) && message.downloaded_media_url && (
                                    <div className="absolute inset-0 bg-black/30 rounded flex items-center justify-center z-10">
                                      <div className="bg-white/90 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                                        <Loader2 size={16} className="animate-spin text-primary" />
                                        <span className="text-xs text-gray-700">Processing...</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {message.message_type === "image" || message.mime_type?.startsWith("image/") ? (
                                    // Prioritize downloaded_media_url - it's accessible without auth
                                    message.downloaded_media_url ? (
                                      <div className="relative">
                                        <img
                                          key={`img-${message.message_id}-${message.downloaded_media_url}`} // Force re-render when URL changes
                                          src={message.downloaded_media_url}
                                          alt="Image"
                                          className="max-w-full max-h-64 rounded object-cover w-full"
                                          onLoad={() => {
                                            // Clear downloading state once image loads successfully
                                            setDownloadingMedia((prev) => {
                                              const next = new Set(prev);
                                              next.delete(message.message_id);
                                              return next;
                                            });
                                          }}
                                          onError={(e) => {
                                            console.error("Error loading downloaded image:", e);
                                            e.target.style.display = "none";
                                            setDownloadingMedia((prev) => {
                                              const next = new Set(prev);
                                              next.delete(message.message_id);
                                              return next;
                                            });
                                          }}
                                        />
                                      </div>
                                    ) : (message.media_url || message.media_id) ? (
                                      // If no downloaded URL but has media_url or media_id, show placeholder
                                      // The download overlay will be shown by the overlay div above
                                      <div className="relative bg-gray-200 rounded flex items-center justify-center min-h-[200px]">
                                        <div className="text-center">
                                          <div className="text-4xl mb-2">📷</div>
                                          <p className="text-xs text-gray-500">
                                            {message.media_id && !message.media_url 
                                              ? "Click to download from Meta" 
                                              : "Image preview unavailable"}
                                          </p>
                                        </div>
                                      </div>
                                    ) : null
                                  ) : message.message_type === "video" || message.mime_type?.startsWith("video/") ? (
                                    (message.downloaded_media_url || message.media_url) ? (
                                      <video
                                        key={message.downloaded_media_url || message.media_url}
                                        src={message.downloaded_media_url || message.media_url}
                                        controls
                                        className="max-w-full max-h-64 rounded w-full"
                                        onLoadedData={() => {
                                          if (message.downloaded_media_url) {
                                            setDownloadingMedia((prev) => {
                                              const next = new Set(prev);
                                              next.delete(message.message_id);
                                              return next;
                                            });
                                          }
                                        }}
                                      >
                                        Your browser does not support the video tag.
                                      </video>
                                    ) : (
                                      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
                                        <div className="text-4xl mb-2">🎥</div>
                                        <p className="text-sm font-medium text-gray-700 mb-3">Video Available</p>
                                        <button
                                          onClick={() => handleDownloadMedia(message)}
                                          disabled={downloadingMedia.has(message.message_id)}
                                          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
                                        >
                                          {downloadingMedia.has(message.message_id) ? (
                                            <>
                                              <Loader2 size={16} className="animate-spin" />
                                              <span>Downloading...</span>
                                            </>
                                          ) : (
                                            <>
                                              <Download size={16} />
                                              <span>Download to View</span>
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    )
                                  ) : message.message_type === "audio" || message.mime_type?.startsWith("audio/") ? (
                                    (message.downloaded_media_url || message.media_url) ? (
                                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                        <span className="text-2xl">🎵</span>
                                        <audio 
                                          key={message.downloaded_media_url || message.media_url}
                                          src={message.downloaded_media_url || message.media_url} 
                                          controls 
                                          className="flex-1" 
                                        />
                                      </div>
                                    ) : (
                                      <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
                                        <div className="text-4xl mb-2">🎵</div>
                                        <p className="text-sm font-medium text-gray-700 mb-3">Audio Available</p>
                                        <button
                                          onClick={() => handleDownloadMedia(message)}
                                          disabled={downloadingMedia.has(message.message_id)}
                                          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
                                        >
                                          {downloadingMedia.has(message.message_id) ? (
                                            <>
                                              <Loader2 size={16} className="animate-spin" />
                                              <span>Downloading...</span>
                                            </>
                                          ) : (
                                            <>
                                              <Download size={16} />
                                              <span>Download to View</span>
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    )
                                  ) : message.message_type === "document" || message.mime_type ? (
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                      <span className="text-3xl">📄</span>
                                      <div className="flex-1">
                                        {message.downloaded_media_url ? (
                                          <a
                                            href={message.downloaded_media_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline break-all block"
                                          >
                                            {message.message_text || "Document"}
                                          </a>
                                        ) : (
                                          <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">{message.message_text || "Document"}</p>
                                            <button
                                              onClick={() => handleDownloadMedia(message)}
                                              disabled={downloadingMedia.has(message.message_id)}
                                              className="px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                            >
                                              {downloadingMedia.has(message.message_id) ? (
                                                <>
                                                  <Loader2 size={14} className="animate-spin" />
                                                  <span>Downloading...</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Download size={14} />
                                                  <span>Download</span>
                                                </>
                                              )}
                                            </button>
                                          </div>
                                        )}
                                        {message.file_size && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {(message.file_size / 1024).toFixed(2)} KB
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="p-4 bg-gray-50 rounded-lg">
                                      {message.downloaded_media_url ? (
                                        <a
                                          href={message.downloaded_media_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
                                        >
                                          <span className="text-xl">📎</span>
                                          <span>View Media</span>
                                        </a>
                                      ) : (
                                        <div className="text-center">
                                          <p className="text-sm font-medium text-gray-700 mb-3">Media file</p>
                                          <button
                                            onClick={() => handleDownloadMedia(message)}
                                            disabled={downloadingMedia.has(message.message_id)}
                                            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
                                          >
                                            {downloadingMedia.has(message.message_id) ? (
                                              <>
                                                <Loader2 size={16} className="animate-spin" />
                                                <span>Downloading...</span>
                                              </>
                                            ) : (
                                              <>
                                                <Download size={16} />
                                                <span>Download to View</span>
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Display message text (for text messages and captions, but not button messages - they're handled above) */}
                              {message.message_text && message.message_type !== "button" && (
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {message.message_text}
                                </p>
                              )}
                              <div
                                className={`flex items-center gap-1 mt-1 ${
                                  message.direction === "outbound"
                                    ? "justify-end"
                                    : "justify-start"
                                }`}
                              >
                                <span
                                  className={`text-[10px] ${
                                    message.direction === "outbound"
                                      ? "text-white/80"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {formatTimestamp(message.sent_at)}
                                </span>
                                {getStatusIcon(message)}
                              </div>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-3 border-t border-gray-200 bg-white">
                    {!isWindowOpen && (
                      <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        ⚠️ Conversation window closed. Use template messages to contact this customer.
                      </div>
                    )}
                    
                    {/* File Preview */}
                    {filePreview && selectedFile && (
                      <div className="mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {filePreview.type === "image" && (
                              <>
                                <ImageIcon size={20} className="text-primary flex-shrink-0" />
                                <img src={filePreview.url} alt="Preview" className="h-12 w-12 object-cover rounded flex-shrink-0" />
                              </>
                            )}
                            {filePreview.type === "video" && (
                              <>
                                <Video size={20} className="text-primary flex-shrink-0" />
                                <video src={filePreview.url} className="h-12 w-12 object-cover rounded flex-shrink-0" />
                              </>
                            )}
                            {filePreview.type === "audio" && (
                              <>
                                <Music size={20} className="text-primary flex-shrink-0" />
                                <audio src={filePreview.url} controls className="flex-1 max-w-xs" />
                              </>
                            )}
                            {filePreview.type === "document" && (
                              <>
                                <File size={20} className="text-primary flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate">{filePreview.name}</p>
                                  <p className="text-xs text-gray-500">{(filePreview.size / 1024).toFixed(2)} KB</p>
                                </div>
                              </>
                            )}
                            <div className="flex-1 min-w-0 ml-2">
                              <p className="text-sm font-medium text-gray-700 truncate">{selectedFile.name}</p>
                              <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button
                            onClick={handleRemoveFile}
                            className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                            type="button"
                          >
                            <X size={16} className="text-gray-600" />
                          </button>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                      {/* File Input Button */}
                      <label className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Paperclip size={20} />
                        <input
                          type="file"
                          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                          onChange={handleFileSelect}
                          disabled={!isWindowOpen || sending || uploadingMedia}
                          className="hidden"
                        />
                      </label>
                      
                      <input
                        type="text"
                        placeholder={isWindowOpen ? (selectedFile ? "Add a caption (optional)..." : "Type a message...") : "Window closed - use templates"}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        disabled={!isWindowOpen || sending || uploadingMedia}
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <button
                        type="submit"
                        disabled={!isWindowOpen || sending || uploadingMedia || (!messageText.trim() && !selectedFile)}
                        className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {(sending || uploadingMedia) ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                        <Send size={16} />
                        )}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveChats;
